<template>
  <StepLayout
    v-if="isSwapEnabled"
    :is-focused
    :tracking="{
      trackingTags: {
        tagDisplayed: 'no_tag',
        tagSelected: false,
      },
      trackingId: 'swap',
      trackingModel: model,
      trackingCategory: category,
    }"
    :without-left-template
  >
    <template #left>
      <RevIllustration
        alt=""
        class="rounded-lg hidden w-full min-w-[337px] max-w-[498px] md:block md:rounded-[32px]"
        :height="498"
        src="/img/buyback/step-trade-in-4.jpg"
        :width="498"
      />
    </template>
    <template #right>
      <fieldset class="flex flex-col">
        <legend class="heading-1 mb-16">
          {{ i18n(translations.titleStep) }}
        </legend>
        <RevIllustration
          alt=""
          class="rounded-lg mb-24 block aspect-[3/2] w-full object-cover object-center md:hidden md:rounded-[32px]"
          :height="218"
          src="/img/buyback/step-trade-in-4.jpg"
          :width="327"
        />

        <CardGuidance
          :clickable="true"
          :icon="BuybackIcon"
          :label="i18n(translations.swapCardGuidance)"
          :modal-name="PRODUCT_MODAL_NAMES.TRADE_IN"
        />

        <template v-if="hasBuybackOfferAvailable">
          <SwapOffer
            :buybackOffer
            :buybackOfferPayload
            :display-guidance="false"
            :model="buybackOffer?.product.shortTitle || selectModel?.label"
            zone="swap_pp_block"
            @default-item-selected="handleDefaultItemSelected"
            @item-selected="handleItemSelected"
            @remove="deleteBuybackOffer"
          />
        </template>

        <template v-else>
          <ul class="list-none">
            <li class="mb-12">
              <div>
                <TradeInSearchBar
                  :label="i18n(translations.swapSelectPlaceholder)"
                  zone="swap_pp_block"
                  @default-item-selected="handleDefaultItemSelected"
                  @item-selected="handleItemSelected"
                />
              </div>
            </li>
            <li>
              <LargePicker
                :index="0"
                :label="i18n(translations.noLabel)"
                :selected="noOptionSelected"
                @click="onClickNoPicker"
              />
            </li>
          </ul>
        </template>
      </fieldset>
    </template>
  </StepLayout>
</template>

<script setup lang="ts">
import { inject, onUnmounted, ref, watch } from 'vue'

import type { Price } from '@backmarket/http-api/src/api-models/Price'
import type { BuybackProduct } from '@backmarket/nuxt-layer-buyback/app/scopes/offer/api/types'
import BuybackIcon from '@backmarket/nuxt-layer-buyback/app/scopes/offer/components/BuybackIcon/BuybackIcon.vue'
import TradeInSearchBar from '@backmarket/nuxt-layer-buyback/app/scopes/offer/components/TradeInSearchBar/TradeInSearchBar.vue'
import { useBuybackOffer } from '@backmarket/nuxt-layer-buyback/app/scopes/offer/composables/useBuybackOffer/useBuybackOffer'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { removeEmptyValuesInObject } from '@backmarket/utils/object/removeEmptyValuesInObject'
import { RevIllustration } from '@ds/components/Illustration'
import { openModal } from '@ds/components/ModalBase'

import type { ErrorData } from '~/scopes/buyback/composables/useBuybackErrorBoundary'
import { MODAL_NAMES } from '~/scopes/buyback/constants'
import { useSwapEnabled } from '~/scopes/buyback/swap/composables/useSwapEnabled/useSwapEnabled'
import { useSwapModalStore } from '~/scopes/buyback/swap/stores/useSwapModalStore'
import CardGuidance from '~/scopes/product-core/components/presentationals/CustomizationFunnel/components/CardGuidance/CardGuidance.vue'
import LargePicker from '~/scopes/product-core/components/presentationals/CustomizationFunnel/components/LargePicker/LargePicker.vue'
import StepLayout from '~/scopes/product-core/components/presentationals/CustomizationFunnel/components/StepLayout/StepLayout.vue'
import { MODAL_NAMES as PRODUCT_MODAL_NAMES } from '~/scopes/product-core/constants'

import { SWAP_INFO_MESSAGE } from '../SwapModal/constants'
import SwapOffer from '../SwapOffer/SwapOffer.vue'

import translations from './SwapStep.translations'

type OptionItem = {
  id: number | string
  label: string
  value: string
}

const props = defineProps<{
  isFocused: boolean
  model?: string
  category?: string
  price?: Price
  withoutLeftTemplate?: boolean
}>()

const i18n = useI18n()
const { isSwapEnabled } = useSwapEnabled()

const { setInitialPayload, resetInitialPayload, setShowAddToCartModal } =
  useSwapModalStore()
const { trackClick } = useTracking()
const {
  deleteBuybackOffer,
  buybackOffer,
  hasBuybackOfferAvailable,
  resetHasNoOffer,
  buybackOfferPayload,
} = useBuybackOffer()

const selectModel = ref<OptionItem | null>(null)
const noOptionSelected = ref(false)

const errorData = inject<ErrorData>('errorData', ref({ message: '', step: '' }))
errorData.value = { message: SWAP_INFO_MESSAGE.SWAP_STEP, step: '' }

watch(
  () => props.isFocused,
  (isFocused) => {
    if (!isFocused) {
      const focusedElement = document.activeElement as HTMLElement
      focusedElement?.blur()
    }
  },
)

function handleDefaultItemSelected() {
  resetHasNoOffer()
  resetInitialPayload()

  openModal(MODAL_NAMES.SWAP_MIX_AND_MATCH)
}

function handleItemSelected(item: BuybackProduct) {
  resetHasNoOffer()
  selectModel.value = {
    id: item.id,
    label: item.model,
    value: item.id,
  }
  noOptionSelected.value = false

  const initialValue = removeEmptyValuesInObject({
    category: item.category,
    brand: item.brand,
    model: item.model,
    model_family: item.productLine,
  })
  if (initialValue) {
    setInitialPayload(initialValue)

    openModal(MODAL_NAMES.SWAP_MIX_AND_MATCH)
  }
}

async function onClickNoPicker() {
  selectModel.value = null
  noOptionSelected.value = true

  resetInitialPayload()
  setShowAddToCartModal(false)
  void deleteBuybackOffer()

  trackClick({
    zone: 'swap',
    name: 'no',
  })
}

onUnmounted(() => {
  resetHasNoOffer()
})
</script>
